import React, { useRef, useState, useEffect } from 'react';
import { Box, Button, Paper, CircularProgress, IconButton, Typography, TextField } from '@mui/material';
import { 
  CameraAlt as CameraIcon, 
  FlipCameraAndroid as FlipIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon 
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import { ocrSpaceAPI } from '../../services/ocrService';

interface CameraViewProps {
  onPlateDetected: (plate: string) => void;
  isScanning: boolean;
}

const CameraView: React.FC<CameraViewProps> = ({ onPlateDetected, isScanning }) => {
  const webcamRef = useRef<Webcam>(null);
  const [processing, setProcessing] = useState(false);
  const [cameraError, setCameraError] = useState<string>('');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [cameraReady, setCameraReady] = useState(false);
  
  // Capture and review workflow
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [detectedText, setDetectedText] = useState<string>('');
  const [ocrConfidence, setOcrConfidence] = useState<number>(0);
  const [manualText, setManualText] = useState<string>('');

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (webcamRef.current?.video) {
        const stream = webcamRef.current.video.srcObject as MediaStream;
        stream?.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    setCameraReady(false);
  };

  const handleCameraError = () => {
    setCameraError('Impossible d\'accéder à la caméra. Veuillez vérifier les permissions.');
  };

  const handleCameraReady = () => {
    setCameraReady(true);
    setCameraError('');
  };

  const capturePhoto = () => {
    if (!webcamRef.current) {
      console.error('Webcam ref not available');
      return;
    }

    // Capture full image first
    const fullImageSrc = webcamRef.current.getScreenshot({
      width: 1920,
      height: 1080
    });
    
    if (!fullImageSrc) {
      console.error('Failed to capture image');
      return;
    }

    console.log('📸 Photo captured, extracting ROI...');
    
    // Extract only the rectangle area (ROI)
    extractROI(fullImageSrc);
  };

  const scanPlate = () => {
    capturePhoto();
  };

  const extractROI = (imageSrc: string) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        console.error('Canvas context not available');
        setCapturedImage(imageSrc); // Fallback to full image
        processImageWithOCR(imageSrc);
        return;
      }

      // ROI dimensions (matches the green rectangle: 70% width, 30% height, centered)
      const roiWidthPercent = 0.70;
      const roiHeightPercent = 0.30;
      
      const roiX = img.width * (1 - roiWidthPercent) / 2;
      const roiY = img.height * (1 - roiHeightPercent) / 2;
      const roiWidth = img.width * roiWidthPercent;
      const roiHeight = img.height * roiHeightPercent;

      // Set canvas to ROI size
      canvas.width = roiWidth;
      canvas.height = roiHeight;

      // Draw only the ROI portion
      ctx.drawImage(
        img,
        roiX, roiY, roiWidth, roiHeight,  // Source rectangle (ROI from full image)
        0, 0, roiWidth, roiHeight          // Destination (fill entire canvas)
      );

      // Convert to data URL
      const roiImageSrc = canvas.toDataURL('image/jpeg', 0.95);
      
      console.log('✂️ ROI extracted:', {
        original: { width: img.width, height: img.height },
        roi: { x: roiX, y: roiY, width: roiWidth, height: roiHeight }
      });

      setCapturedImage(roiImageSrc);
      setManualText('');
      
      // Start OCR processing on ROI only
      processImageWithOCR(roiImageSrc);
    };

    img.onerror = () => {
      console.error('Failed to load image for ROI extraction');
      setCapturedImage(imageSrc); // Fallback to full image
      processImageWithOCR(imageSrc);
    };

    img.src = imageSrc;
  };

  const processImageWithOCR = async (imageSrc: string) => {
    setProcessing(true);
    setDetectedText('');
    setOcrConfidence(0);
    
    try {
      console.log('🔍 Starting OCR with OCR.space API...');
      
      // Call OCR.space API
      const result = await ocrSpaceAPI(imageSrc);

      console.log('✅ OCR Complete!');
      console.log('Raw text:', result.text);
      console.log('Confidence:', result.confidence);
      
      // Clean up the text
      const cleanedText = result.text
        .toUpperCase()
        .replace(/\s+/g, '') // Remove all spaces
        .replace(/\n+/g, '') // Remove line breaks
        .replace(/[^A-Z0-9-]/g, ''); // Keep only letters, numbers, and dashes
      
      setDetectedText(result.text);
      setOcrConfidence(result.confidence);
      setManualText(cleanedText);
      
      console.log('Cleaned text:', cleanedText);
      
    } catch (err) {
      console.error('❌ OCR error:', err);
      setDetectedText('OCR indisponible - Saisie manuelle requise');
      setOcrConfidence(0);
      setManualText(''); // Clear manual text to prompt user input
    } finally {
      setProcessing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage('');
    setDetectedText('');
    setOcrConfidence(0);
    setManualText('');
  };

  const confirmPlate = () => {
    if (manualText.trim()) {
      console.log('✓ Plate confirmed:', manualText);
      onPlateDetected(manualText.trim().toUpperCase());
      
      // Reset after confirmation
      setCapturedImage('');
      setDetectedText('');
      setManualText('');
    }
  };

  // If we have a captured image, show review screen
  if (capturedImage) {
    return (
      <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <Box sx={{ position: 'relative', width: '100%', minHeight: '200px', bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Captured ROI Image */}
          <Box
            component="img"
            src={capturedImage}
            alt="Captured ROI"
            sx={{
              maxWidth: '100%',
              maxHeight: '200px',
              objectFit: 'contain',
              border: '2px solid',
              borderColor: 'success.main',
              borderRadius: 1
            }}
          />

          {/* Processing Overlay */}
          {processing && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
              }}
            >
              <CircularProgress size={60} sx={{ color: 'white' }} />
              <Typography variant="body1" sx={{ color: 'white' }}>
                Analyse en cours...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Results Section */}
        {!processing && (
          <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
            {/* Error Message */}
            {detectedText.includes('OCR indisponible') && (
              <Box sx={{ 
                p: 2, 
                mb: 2, 
                bgcolor: 'warning.light', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main'
              }}>
                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                  ⚠️ OCR temporairement indisponible
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Veuillez saisir manuellement le numéro de plaque
                </Typography>
              </Box>
            )}

            {/* Manual Edit Field */}
            <TextField
              fullWidth
              label="Numéro de plaque"
              value={manualText}
              onChange={(e) => setManualText(e.target.value.toUpperCase())}
              placeholder={detectedText.includes('OCR indisponible') ? "Saisissez le numéro de plaque" : "Modifiez si nécessaire"}
              variant="outlined"
              size="small"
              sx={{ mb: 1.5 }}
              helperText={detectedText.includes('OCR indisponible') ? "Saisie manuelle requise" : "Corrigez le texte si nécessaire"}
            />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={retakePhoto}
                sx={{ flex: 1 }}
              >
                Reprendre
              </Button>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CheckIcon />}
                onClick={confirmPlate}
                disabled={!manualText.trim()}
                sx={{ flex: 1 }}
              >
                Valider
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    );
  }

  // Camera View
  return (
    <Paper elevation={2} sx={{ overflow: 'hidden', borderRadius: 2 }}>
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4/3', bgcolor: 'black' }}>
        {cameraError ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="error">
              {cameraError}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Webcam Component */}
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                facingMode: facingMode,
                width: { ideal: 1920 },
                height: { ideal: 1080 }
              }}
              onUserMedia={handleCameraReady}
              onUserMediaError={handleCameraError}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              forceScreenshotSourceSize={true}
            />

            {/* Camera Flip Button */}
            <IconButton
              onClick={toggleCamera}
              disabled={!cameraReady}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                }
              }}
            >
              <FlipIcon />
            </IconButton>

            {/* Scanning Guide Overlay */}
            {cameraReady && isScanning && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '35%',
                  left: '15%',
                  width: '70%',
                  height: '30%',
                  border: '3px solid',
                  borderColor: processing ? 'warning.main' : 'success.main',
                  borderRadius: 2,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none',
                }}
              >
                {/* Corner guides */}
                <Box sx={{ position: 'absolute', top: -3, left: -3, width: 20, height: 20, borderTop: '3px solid white', borderLeft: '3px solid white' }} />
                <Box sx={{ position: 'absolute', top: -3, right: -3, width: 20, height: 20, borderTop: '3px solid white', borderRight: '3px solid white' }} />
                <Box sx={{ position: 'absolute', bottom: -3, left: -3, width: 20, height: 20, borderBottom: '3px solid white', borderLeft: '3px solid white' }} />
                <Box sx={{ position: 'absolute', bottom: -3, right: -3, width: 20, height: 20, borderBottom: '3px solid white', borderRight: '3px solid white' }} />
              </Box>
            )}

            {/* Processing Indicator */}
            {processing && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CircularProgress size={40} sx={{ color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Analyse...
                </Typography>
              </Box>
            )}

            {/* Scan Button Overlay */}
            {cameraReady && isScanning && !processing && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CameraIcon />}
                  onClick={scanPlate}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '50px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                    }
                  }}
                >
                  Scanner
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Camera Status */}
      {!cameraReady && !cameraError && (
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            Initialisation de la caméra...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CameraView;
